import {
  ApplicationModel,
  ApplicationsClient,
  ClientClient,
  ClientModel,
  ContactClient,
  ContactModel,
  CreateApplication,
  CreateMaterial,
  CreateQuantity,
  CreateQuarry,
  CreateUser,
  CustomerFilterSort,
  CustomerModel,
  CustomersClient,
  CustomerSortField,
  JobSiteClient,
  JobSiteFilterSort,
  JobSiteModel,
  JobSiteSortField,
  MaterialModel,
  MaterialsClient,
  QuantitiesClient,
  QuantityModel,
  QuarriesClient,
  QuarryModel,
  UpdateContactModel,
  UpdateCustomerModel,
  UpdateJobSiteModel,
  UpdateUser,
  UserClient,
  UserFilterSort,
  UserModel,
  UserRole,
  UserSortField
} from "@/core/api/apiClient";
const AdminService = {
  /**
   * Gets all Applications.
   *
   * @param {string} clientId Client's ID
   * @returns {Promise<IApplication[] | any>} Promise-wrapped data.
   * @author Nick Brahimir and Kelly Spriggs
   */
  async getAllApplications(clientId: string): Promise<ApplicationModel[] | any> {
    const applicationsClient = new ApplicationsClient();
    return await applicationsClient
      .listForClient(clientId)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Creates a new Application.
   *
   * @param {string} clientId The Client's ID
   * @param {CreateApplication} payload The new Application
   * @return {*}  {Promise<any>} Promise-wrapped response.
   */
  async createApplication(clientId: string, payload: CreateApplication): Promise<any> {
    const applicationsClient = new ApplicationsClient();

    return await applicationsClient
      .create(clientId, payload)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Updates an Application
   *
   * @param {string} clientId The client id
   * @param {ApplicationModel} payload The new application payload
   * @returns {Promise<any>} Promie-wrapped reponse
   * @author Nick Brahmimir
   */
  async updateApplication(clientId: string, payload: ApplicationModel): Promise<any> {
    const applicationsClient = new ApplicationsClient();

    return await applicationsClient
      .update(clientId, payload.applicationId, payload)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Deletes an Application
   *
   * @param {string} clientId The client id
   * @param {string} id The application id to delete
   * @returns {Promise<any>} Promise-wrapped response
   * @author Nick Brahmimir
   */
  async deleteApplication(clientId: string, id: string): Promise<any> {
    const applicationsClient = new ApplicationsClient();

    return await applicationsClient.delete(clientId, id).catch((error) => {
      console.error(error);
    });
  },
  /**
   * Gets all Materials.
   *
   * @param {string} clientId Client's ID
   * @returns {Promise<MaterialModel[] | any>} Promise-wrapped data.
   * @author Nick Brahimir and Kelly Spriggs
   */
  async getAllMaterials(clientId: string): Promise<MaterialModel[] | any> {
    const materialsClient = new MaterialsClient();
    return await materialsClient
      .listForClient(clientId)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Creates a new Material
   *
   * @param {string} clientId The Client's ID
   * @param {MaterialModel} payload The new Material
   * @return {*}  {Promise<any>} Promise-wrapped response.
   */
  async createMaterial(clientId: string, payload: CreateMaterial): Promise<any> {
    const materialsClient = new MaterialsClient();

    return await materialsClient
      .create(clientId, payload)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Updates a Material
   *
   * @param {string} clientId The client id
   * @param {MaterialModel} payload The updated material
   * @returns {Promise<any>} Promise-wrapped response
   */
  async updateMaterial(clientId: string, payload: MaterialModel): Promise<any> {
    const materialsClient = new MaterialsClient();

    return await materialsClient
      .update(clientId, payload.materialId, payload)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Deletes a Material
   *
   * @param {string} clientId The client id
   * @param {string} id The material id to delete
   * @returns {Promise<any>} Promise-wrapped response
   */
  async deleteMaterial(clientId: string, id: string): Promise<any> {
    const materialsClient = new MaterialsClient();

    return await materialsClient.delete(clientId, id).catch((error) => {
      console.error(error);
    });
  },
  /**
   * Gets all Quantities.
   *
   * @param {string} clientId Client's ID
   * @returns {Promise<QuantityModel[] | any>} Promise-wrapped data.
   * @author Nick Brahimir and Kelly Spriggs
   */
  async getAllQuantities(clientId: string): Promise<QuantityModel[] | any> {
    const quantitiesClient = new QuantitiesClient();
    return await quantitiesClient
      .listForClient(clientId)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Creates a new Quantity.
   *
   * @param {string} clientId The Client's ID.
   * @param {CreateQuantity} payload The new Quantity.
   * @return {*}  {Promise<any>} Promise-wrapped response.
   */
  async createQuantity(clientId: string, payload: CreateQuantity): Promise<any> {
    const quantitiesClient = new QuantitiesClient();

    return await quantitiesClient
      .create(clientId, payload)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Updates a Quantity
   *
   * @param {string} clientId The client id
   * @param {QuantityModel} payload The new quantity payload
   * @returns {Promise<any>} Promise-wrapped response
   * @author Nick Brahimir
   */
  async updateQuantity(clientId: string, payload: QuantityModel): Promise<any> {
    const quantitiesClient = new QuantitiesClient();

    return await quantitiesClient
      .update(clientId, payload.quantityId, payload)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Deletes a Quantity
   *
   * @param {string} clientId The client id
   * @param {string} id The id of the quantity to delete
   * @returns {Promise<any>} Promise-wrapped response
   */
  async deleteQuantity(clientId: string, id: string): Promise<any> {
    const quantitiesClient = new QuantitiesClient();

    return await quantitiesClient.delete(clientId, id).catch((error) => {
      console.error(error);
    });
  },
  /**
   * Gets all Users.
   *
   * @param {string} clientId Client's ID
   * @returns {Promise<UserModel[] | any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async getAllUsers(clientId: string): Promise<UserModel[] | undefined> {
    const userClient = new UserClient();

    const filter = new UserFilterSort({
      clientId: clientId,
      excludeRoles: [UserRole.SystemUser],
      sortDescending: false,
      sortField: UserSortField.LastName
    });

    try {
      return (await userClient.list(filter)).model;
    } catch (ex) {
      console.error(ex);
    }
  },

  async createUser(newUser: CreateUser): Promise<UserModel | undefined> {
    const userClient = new UserClient();
    return (await userClient.create(newUser.clientId, newUser)).model;
  },

  async updateUser(user: UpdateUser): Promise<UserModel | undefined> {
    const userClient = new UserClient();
    return (await userClient.update(user.clientId, user.userId, user)).model;
  },

  async deleteUser(clientId: string, userId: string): Promise<void> {
    const userClient = new UserClient();
    await userClient.delete(clientId, userId);
  },
  /**
   * Gets all Customers, ordered by their LastUsedTimestamp.
   *
   * @param {string} clientId Client's ID
   * @param {boolean} [sortTimeStamp] Boolean to sort by TimeStamp
   * @returns {Promise<CustomerModel[] | any>} Promise-wrapped data.
   * @author Nick Brahimir and Kelly Spriggs
   */
  async getAllCustomers(clientId: string, sortTimeStamp?: boolean): Promise<CustomerModel[] | any> {
    const customersClient = new CustomersClient();
    const filter = new CustomerFilterSort({
      clientId: clientId,
      sortField: sortTimeStamp ? CustomerSortField.LastUsedTimestamp : CustomerSortField.Name,
      sortDescending: sortTimeStamp ? true : false
    });

    return await customersClient
      .listForClient(filter)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Creates a Customer.
   *
   * @param {string} clientId Client's ID
   * @param {UpdateCustomerModel} payload The Customer
   * @returns {Promise<any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async createCustomer(clientId: string, payload: UpdateCustomerModel): Promise<any> {
    const customerClient = new CustomersClient();

    return customerClient
      .addCustomer(clientId, payload)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Updates a Customer.
   *
   * @param {string} clientId Client's ID
   * @param {string} customderId Customer's ID
   * @param {UpdateCustomerModel} payload The Customer
   * @returns {Promise<any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async updateCustomer(
    clientId: string,
    customerId: string,
    payload: UpdateCustomerModel
  ): Promise<any> {
    const customerClient = new CustomersClient();

    return customerClient
      .updateCustomer(clientId, customerId, payload)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Deletes a Customer.
   *
   * @param {string} clientId Client's ID
   * @param {string} customerId The Customer's ID
   * @returns {Promise<any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async deleteCustomer(clientId: string, customerId: string): Promise<any> {
    const customerClient = new CustomersClient();

    return customerClient
      .deleteCustomer(clientId, customerId)
      .then(() => {
        const success = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Gets all Contacts for a Customer.
   *
   * @param {string} clientId Client's ID
   * @returns {Promise<ContactModel[] | any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async getAllCustomerContacts(clientId: string, customerId: string): Promise<ContactModel[] | any> {
    const customersClient = new CustomersClient();

    return await customersClient
      .listContactsForClient(clientId, customerId)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Creates a Customer Contact.
   *
   * @param {string} clientId Client's ID
   * @param {ICustomer} payload The Customer
   * @returns {Promise<any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async createCustomerContact(
    clientId: string,
    customerId: string,
    payload: UpdateContactModel
  ): Promise<any> {
    const contactClient = new ContactClient();

    return contactClient
      .createContact(clientId, customerId, payload)
      .then(() => {
        const success: any = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },
  /**
   * Updates a Customer Contact.
   *
   * @param {string} clientId Client's ID
   * @param {string} customerId Customer's ID
   * @param {ContactModel} payload The Contact data
   * @returns {Promise<any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async updateCustomerContact(
    clientId: string,
    customerId: string,
    payload: ContactModel
  ): Promise<any> {
    const contactClient = new ContactClient();

    const updateContact = new UpdateContactModel({
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      ext: payload.ext
    });

    return contactClient
      .updateContact(clientId, customerId, payload.contactId, updateContact)
      .then(() => {
        const success: any = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },
  /**
   * Deletes a Customer Contact.
   *
   * @param {string} clientId Client's ID
   * @param {string} customerId The Customer ID
   * @param {string} contactId The contact ID
   * @returns {Promise<any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async deleteCustomerContact(clientId: string, customerId: string, id: string): Promise<any> {
    const contactClient = new ContactClient();

    return contactClient
      .deleteContact(clientId, customerId, id)
      .then(() => {
        const success: any = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },
  /**
   * Creates a Customer JobSite.
   *
   * @param {string} clientId Client's ID
   * @param {ICustomer} payload The Customer
   * @returns {Promise<any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async createCustomerJobSite(
    clientId: string,
    customerId: string,
    payload: UpdateJobSiteModel
  ): Promise<any> {
    const jobsiteClient = new JobSiteClient();

    return jobsiteClient
      .createJobsite(clientId, customerId, payload)
      .then(() => {
        const success: any = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },
  /**
   * Updates a Customer JobSite.
   *
   * @param {string} clientId Client's ID
   * @param {string} customerId Customer's ID
   * @param {UpdateJobSiteModel} payload The Customer
   * @returns {Promise<any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async updateCustomerJobSite(
    clientId: string,
    customerId: string,
    jobsiteId: string,
    payload: UpdateJobSiteModel
  ): Promise<any> {
    const jobSiteClient = new JobSiteClient();

    return jobSiteClient
      .updateJobsite(clientId, customerId, jobsiteId, payload)
      .then(() => {
        const success: any = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },
  /**
   * Deletes a Customer JobSite.
   *
   * @param {string} clientId Client's ID
   * @param {ICustomer} payload The Customer
   * @returns {Promise<any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async deleteCustomerJobSite(clientId: string, customerId: string, jobSiteId: string): Promise<any> {
    const jobSiteClient = new JobSiteClient();

    return jobSiteClient
      .deleteJobsite(clientId, customerId, jobSiteId)
      .then(() => {
        const success: any = {
          status: 204,
          message: "OK"
        };
        return success;
      })
      .catch((error: any) => {
        console.error(error);
      });
  },
  /**
   * Gets all Jobsites for a Customer.
   *
   * @param {string} clientId Client's ID
   * @returns {Promise<JobSiteModel[] | any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async getAllCustomerJobsites(
    clientId: string,
    customerId: string,
    sortTimeStamp?: boolean
  ): Promise<JobSiteModel[] | any> {
    const customersClient = new CustomersClient();

    const filter = new JobSiteFilterSort({
      clientId: clientId,
      customerId: customerId,
      sortField: sortTimeStamp ? JobSiteSortField.LastUsedTimestamp : JobSiteSortField.Name,
      sortDescending: sortTimeStamp ? true : false
    });

    return await customersClient
      .listJobSitesForClient(filter)
      .then((data) => {
        const sites: JobSiteModel[] = [];
        data.model.forEach((jobSite) => {
          if (!jobSite.name) jobSite.name = jobSite.streetAddress ?? "Jobsite unnamed";
          sites.push(jobSite);
        });
        return sites;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  async createClient(): Promise<void> {
    // TODO
  },
  /**
   * Updates a Client.
   *
   * @param {string} clientId Client's ID
   * @param {ClientModel} data Client object
   * @returns {Promise<ClientModel | undefined>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async updateClient(clientId: string, data: ClientModel): Promise<ClientModel | undefined> {
    const clientClient = new ClientClient();

    return await clientClient
      .updateClient(clientId, data)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
        return undefined;
      });
  },

  /**
   * Updates a client's logo
   */
  async updateClientLogo(clientId: string, logo: File): Promise<ClientModel | undefined> {
    const clientClient = new ClientClient();

    return await clientClient
      .setClientLogo(clientId, { fileName: logo.name, data: logo })
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
        return undefined;
      });
  },
  async deleteClient(): Promise<void> {
    // TODO
  },
  /**
   * Gets all Users for a Client.
   *
   * @param {string} clientId Client's ID
   * @returns {Promise<UserModel[] | any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async getAllClientUsers(clientId: string): Promise<UserModel[] | any> {
    const userClient = new UserClient();

    const filter = new UserFilterSort({
      clientId: clientId,
      excludeRoles: [],
      sortDescending: false,
      sortField: UserSortField.LastName
    });

    try {
      return (await userClient.list(filter)).model;
    } catch (ex) {
      console.error(ex);
    }
  },

  /**
   * Gets all Quarries.
   *
   * @param {string} clientId Client's ID
   * @returns {Promise<QuarryModel[] | any>} Promise-wrapped data.
   * @author Nick Brahimir
   */
  async getAllQuarries(clientId: string): Promise<QuarryModel[] | any> {
    const quarriesClient = new QuarriesClient();

    return await quarriesClient
      .listForClient(clientId)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Creates a new Quarry
   *
   * @param {string} clientId The Client ID
   * @param {CreateQuarry} payload The new Quarry
   * @returns {Promise<any>} Promise-wrapped response
   * @author Nick Brahimir
   */
  async createQuarry(clientId: string, payload: CreateQuarry): Promise<any> {
    const quarriesClient = new QuarriesClient();

    return await quarriesClient
      .create(clientId, payload)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Updates a Quarry
   *
   * @param {string} clientId The Client ID
   * @param {QuarryModel} payload The new Quarry data
   * @returns {Promise<any>} Promise-wrapped response.
   * @author Nick Brahimir
   */
  async updateQuarry(clientId: string, payload: QuarryModel): Promise<any> {
    const quarriesClient = new QuarriesClient();

    return await quarriesClient
      .update(clientId, payload.quarryId, payload)
      .then((data) => {
        return data.model;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  /**
   * Deletes a Quarry
   *
   * @param {string} clientId The Client ID
   * @param {string} quarryId The Quarry ID
   * @returns {Promise<any>} Promise-wrapped response.
   * @author Nick Brahimir
   */
  async deleteQuarry(clientId: string, quarryId: string): Promise<any> {
    const quarriesClient = new QuarriesClient();

    return await quarriesClient.delete(clientId, quarryId).catch((error) => {
      console.error(error);
    });
  }
};
export default AdminService;
